import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Map, Marker, Popup } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { getTrafficCameraById, getTrafficSignalsById, getCrashData, searchIncidents, getWorkflows } from '../../api';

import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    Checkbox,
    FormControlLabel,
    Grid, Card, CardContent, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import IncidentPieChart from '../Charts/PieChartByType'; // Pie chart component
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { AssetType, MAP_BOX_API_KEY } from '../../services/AppConstant';
import CameraPreview from './CameraPreview';
import TrafficLightPreview from './TrafficLightPreview';

// Import custom icons
import cameraIcon from '../../assets/camera_blue.png';
import trafficSignalIcon from '../../assets/red_signal.png';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import IncidentScatterPlot from '../Charts/ScatterPlot';

// Apply amChart theme
am4core.useTheme(am4themes_animated);

const AssetDetailPage = () => {
    const { assetId, type } = useParams();
    const navigate = useNavigate();
    const [assetDetails, setAssetDetails] = useState(null);
    const [incidents, setIncidents] = useState([]);
    const [workflows, setWorkflows] = useState([]);
    const [crashes, setCrashes] = useState([]);
    const [totalCrashes, setTotalCrashes] = useState(0);
    const [totalIncidents, setTotalIncidents] = useState(0);

    const [incidentPage, setIncidentPage] = useState(0);
    const [incidentRowsPerPage, setIncidentRowsPerPage] = useState(5);
    const [crashPage, setCrashPage] = useState(0);
    const [crashRowsPerPage, setCrashRowsPerPage] = useState(5);
    const [latitude, setLatitude] = useState(30.2672);
    const [longitude, setLongitude] = useState(-97.7431);

    const [showCrashes, setShowCrashes] = useState(true);
    const [selectedCrash, setSelectedCrash] = useState(null);
    useEffect(() => {
        const fetchAssetDetails = async () => {
            try {
                const response = type === AssetType.CAMERA ? await getTrafficCameraById(assetId) : await getTrafficSignalsById(assetId);
                setAssetDetails(response.data);
                setLatitude(response.data['Location'].coordinates[1]);
                setLongitude(response.data['Location'].coordinates[0]);
            } catch (error) {
                console.error('Error fetching incidents:', error);
            }
        };
        fetchAssetDetails()

      

    }, [assetId]);

    useEffect(() => {
        const fetchIncidents = async () => {
            try {
                const response = await searchIncidents({
                    sourceId: assetId,
                    sourceType: type,
                    page: incidentPage + 1,
                    limit: incidentRowsPerPage,
                });
                setIncidents(response.data.incidents);
                setTotalIncidents(response.data.totalRecords);
            } catch (error) {
                console.error('Error fetching incidents:', error);
            }
        };
        fetchIncidents();
    }, [assetDetails, incidentRowsPerPage, incidentPage]);

    useEffect(() => {

        const fetchWorkflows = async () => {
            try {
                const response = await getWorkflows({
                    assetId: assetId
                });
                setWorkflows(response.data.data);
            } catch (error) {
                console.error('Error fetching workflows:', error);
            }
        };



        fetchWorkflows();

    }, [assetDetails]);

    useEffect(() => {
        const fetchCrashes = async () => {
            if (!assetDetails) return;
            try {
                const response = await getCrashData({
                    lat: latitude,
                    lng: longitude,
                    distance: 0.0311, // 100 meters (converted to km)
                    page: crashPage + 1,
                    limit: crashRowsPerPage,
                });
                setCrashes(response.data.records);
                setTotalCrashes(response.data.totalRecords);
            } catch (error) {
                console.error('Error fetching crashes:', error);
            }
        };
        fetchCrashes();
    }, [assetDetails, crashPage, crashRowsPerPage]);

    useEffect(() => {
        let chart = am4core.create("chartdiv", am4charts.XYChart);
        chart.data = incidents.map((incident, index) => ({
            date: new Date(incident.createdAt),
            count: index + 1, // Simulate some counts for visualization
        }));

        let dateAxis = chart.xAxes.push(new am4charts.DateAxis());
        dateAxis.renderer.minGridDistance = 50;
        dateAxis.title.text = "Date";

        let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.title.text = "Incident Count";

        let series = chart.series.push(new am4charts.LineSeries());
        series.dataFields.valueY = "count";
        series.dataFields.dateX = "date";
        series.tooltipText = "{date}: [b]{count}[/]";
        series.strokeWidth = 2;
        series.minBulletDistance = 10;

        chart.cursor = new am4charts.XYCursor();
        chart.cursor.behavior = "panX";
        chart.scrollbarX = new am4core.Scrollbar();

        return () => {
            chart.dispose();
        };
    }, [incidents]);
    const handleRowClick = (incidentId) => {
        navigate(`/incident/${incidentId}`); // Navigate to detail page with incident ID
    };

    if (!assetDetails) return <div>Loading...</div>;


    return (
        <div style={{ padding: "0px 20px" }}>
            <h2>Asset Details: {assetDetails['Location Name']}</h2>
            {type === AssetType.CAMERA ? <CameraPreview cameraDetails={assetDetails} /> : <TrafficLightPreview trafficLightDetails={assetDetails} ></TrafficLightPreview>}

            {/* Map Section */}
            {/* Checkbox to show or hide crash markers */}
            <FormControlLabel
                control={<Checkbox checked={showCrashes} onChange={() => setShowCrashes(!showCrashes)} />}
                label="Show Traffic Crashes"
            />

            {/* Map Section */}
            <section style={{ marginBottom: "20px" }}>
                <Map
                    initialViewState={{
                        latitude,
                        longitude,
                        zoom: 14,
                    }}
                    style={{ width: "100%", height: "300px" }}
                    mapStyle="mapbox://styles/mapbox/streets-v11"
                    mapboxAccessToken={MAP_BOX_API_KEY}
                    onClick={() => setSelectedCrash(null)}
                >
                    {/* Marker for the main asset (camera or traffic signal) */}
                    <Marker latitude={latitude} longitude={longitude}>
                        <img
                            src={type === AssetType.CAMERA ? cameraIcon : trafficSignalIcon}
                            alt="Asset Icon"
                            style={{ width: '30px', height: '30px' }}
                        />
                    </Marker>

                    {/* Conditional Markers for each crash with tooltip */}
                    {showCrashes && crashes.map((crash) => (
                        <Marker
                            key={crash._id}
                            latitude={crash.latitude}
                            longitude={crash.longitude}
                            onMouseEnter={() => setSelectedCrash(crash)}
                            onMouseLeave={() => setSelectedCrash(null)}
                        >
                            <ErrorOutlineIcon style={{ fontSize: 25, color: 'red' }} />

                        </Marker>
                    ))}

                    {/* Popup for crash marker tooltip */}
                    {selectedCrash && (
                        <Popup
                            latitude={selectedCrash.latitude}
                            longitude={selectedCrash.longitude}
                            closeButton={false}
                            anchor="top"
                            onClose={() => setSelectedCrash(null)}
                        >
                            <div>
                                <strong>Crash ID:</strong> {selectedCrash["Crash ID"]}<br />
                                <strong>Date:</strong> {new Date(selectedCrash.timestamp).toLocaleString()}<br />
                                <strong>Address:</strong> {selectedCrash["Primary address"]}
                            </div>
                        </Popup>
                    )}
                </Map>
            </section>

            {/* Incident Table */}
            <section style={{ marginBottom: "20px" }}>
                <h2>Incidents</h2>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Created At</TableCell>
                                <TableCell>Workflow Name</TableCell>
                                <TableCell>Event Name</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {incidents.map((incident) => (
                                <TableRow
                                    key={incident._id}
                                    onClick={() => handleRowClick(incident._id)} // Add click handler
                                    style={{ cursor: 'pointer' }}
                                >
                                    <TableCell>{incident.sourceType}</TableCell>
                                    <TableCell>{incident.sourceId['ATD Location ID']}</TableCell>

                                    <TableCell>{new Date(incident.createdAt).toLocaleString()}</TableCell>
                                    <TableCell>{incident.workflowId?.name || 'N/A'}</TableCell>
                                    <TableCell>{incident.eventId?.type || 'N/A'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={totalIncidents}
                    page={incidentPage}
                    onPageChange={(e, newPage) => setIncidentPage(newPage)}
                    rowsPerPage={incidentRowsPerPage}
                    onRowsPerPageChange={(e) => setIncidentRowsPerPage(parseInt(e.target.value, 10))}
                    rowsPerPageOptions={[5, 10, 25]}
                />
            </section>
            {/* Crash Table */}
            <section style={{ marginBottom: "20px" }}>
                <h2>Nearesh Traffic Crashes - 50m </h2>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Crash ID</TableCell>
                                <TableCell>Crash Date</TableCell>
                                <TableCell>Primary Address</TableCell>
                                <TableCell>Total Injuries</TableCell>
                                <TableCell>Death Count</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {crashes.map((crash) => (
                                <TableRow key={crash._id}>
                                    <TableCell>{crash["Crash ID"]}</TableCell>
                                    <TableCell>{new Date(crash.timestamp).toLocaleString()}</TableCell>
                                    <TableCell>{crash["Primary address"]}</TableCell>
                                    <TableCell>{crash.tot_injry_cnt}</TableCell>
                                    <TableCell>{crash.death_cnt}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={totalCrashes}
                    page={crashPage}
                    onPageChange={(e, newPage) => setCrashPage(newPage)}
                    rowsPerPage={crashRowsPerPage}
                    onRowsPerPageChange={(e) => setCrashRowsPerPage(parseInt(e.target.value, 10))}
                    rowsPerPageOptions={[5, 10, 25]}
                />
            </section>

            {/* Workflow Table */}
            <section style={{ marginBottom: "20px" }}>
                <h2>Workflows</h2>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Workflow ID</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Description</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {workflows.map((workflow) => (
                                <TableRow key={workflow._id}>
                                    <TableCell>{workflow._id}</TableCell>
                                    <TableCell>{workflow.name}</TableCell>
                                    <TableCell>{workflow.description}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </section>



            {incidents.length > 0 ?
                <Grid container spacing={3} style={{ padding: '20px' }}>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6">Incident Scatter Plot (Last 24 Hours)</Typography>
                                <IncidentScatterPlot
                                    data={incidents.map(incident => ({
                                        date: new Date(incident.createdAt).toISOString().split('T')[0],
                                        time: new Date(incident.createdAt).toISOString().split('T')[1].slice(0, 5),
                                        type: incident.eventId?.type || 'N/A',
                                    }))}
                                />
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6">Incident Distribution by Type</Typography>
                                <IncidentPieChart data={incidents.map(incident => ({
                                    date: new Date(incident.createdAt).toISOString().split('T')[0],
                                    time: new Date(incident.createdAt).toISOString().split('T')[1].slice(0, 5),
                                    type: incident.eventId?.type || 'N/A',
                                }))}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                : null}

        </div>
    );
};

export default AssetDetailPage;
